import {
  Component,
  Mixins,
  Prop,
  Ref,
  Vue,
  Watch,
} from 'vue-property-decorator'
import cytoscape from '@/app/cytoscape'
import DiagramService from '@/app/services/diagram-data-service'
import saveAs from 'file-saver'
import style from '@/diagrams/configs/topic-spider-style'
import { diagramViews, DiagramView } from '@/diagrams/models/diagram-views'
import Node from '@/diagrams/models/node'
import EditNodeEvent from '@/diagrams/events/edit-node-event'
import CreateNodeEvent from '@/diagrams/events/create-node-event'
import cytoService from '@/diagrams/services/cyto-service'
import ProjectsService from '@/projects/services/projects-service'
import Project from '@/projects/entities/project'
import { ProjectStatus } from '@/projects/entities/project-status'
import { EventObject, NodeSingular } from 'cytoscape'
import SpiderData from '../../models/spider-data'
import Visual from '@/diagrams/models/visual'
import Nodes from '@/diagrams/utils/nodes'

@Component
export default class Spider extends Vue implements Visual {
  private PADDING = 50

  private action: string = diagramViews.items[0].url
  private project: Project | null = null
  private selected: Node | null = null
  private sourceGroup: string = ''
  private isMultiDiagram = false
  private view: 'both' | 'first' | 'second' = 'both'
  private data: SpiderData | null = null
  private hasPositive = false
  private hasNegative = false
  private hasWinner = false
  private hasPassive = false

  get archived() {
    return (
      this.project !== null && this.project.status === ProjectStatus.Archived
    )
  }

  get spiderType() {
    switch (this.action) {
      case 'projectTopicGroupsTopics':
        return 'themen'
      case 'projectStakeholderGroupStakeholders':
        return 'stakeholder'
      case 'projectActionGroupsActions':
        return 'maßnahmen'
    }
    return 'unbekannt'
  }

  get showFirst() {
    return this.view === 'both' || this.view === 'first'
  }

  get showSecond() {
    return this.view === 'both' || this.view === 'second'
  }

  public async setActionAsync(ctx: DiagramView) {
    this.action = ctx.url
    await this.reloadAsync()
  }

  // === EXPORT ===

  public async saveAsync(format: string) {
    this.saveInstance(window.cyTopic1, format, 0)
    if (this.isMultiDiagram) {
      this.saveInstance(window.cyTopic2, format, 1)
    }
  }

  public saveInstance(instance: cytoscape.Core, format: string, num: number) {
    if (instance) {
      this.addTitle(instance, this.diagramTitle())
      this.addLegend(instance)

      // export with bias
      switch (format) {
        case 'svg':
          this.saveSVG(instance, num, '')
          break
        default:
          this.savePNG(instance, num, '')
          break
      }

      // export without bias
      this.removeBiasLegend(instance)
      const savedBiases: Array<{
        node: NodeSingular
        bias: number
        attitude: number
      }> = []
      instance.nodes().forEach(function (n) {
        savedBiases.push({
          node: n,
          bias: n.data('bias'),
          attitude: n.data('attitude'),
        })
        n.data('bias', 0)
        n.data('attitude', 0)
      })
      switch (format) {
        case 'svg':
          this.saveSVG(instance, num, '-OB')
          break
        default:
          this.savePNG(instance, num, '-OB')
          break
      }
      savedBiases.forEach(function (nb) {
        nb.node.data('bias', nb.bias)
        nb.node.data('attitude', nb.attitude)
      })

      this.removeTitleAndLegend(instance)
    }
  }

  private diagramTitle() {
    switch (this.action) {
      case 'projectTopicGroupsTopics':
        return 'Spinne - Themendimension/Thema'
      case 'projectStakeholderGroupStakeholders':
        return 'Spinne - Stakeholdergruppe/Stakeholder'
      case 'projectActionGroupsActions':
        return 'Spinne - Maßnahmengruppe/Maßnahme'
      case 'projectTopicsActions':
        return 'Spinne - Thema/Maßnahme'
      case 'projectStakeholdersActions':
        return 'Spinne - Stakeholder/Maßnahme'
      case 'projectTopicsStakeholders':
        return 'Spinne - Thema/Stakeholder'
      case 'projectStakeholdersTopics':
        return 'Spinne - Stakeholder/Thema'
      case 'projectActionsStakeholders':
        return 'Spinne - Maßnahme/Stakeholder'
      default:
        return ''
    }
  }

  private removeBiasLegend(instance: cytoscape.Core) {
    instance.nodes().forEach((ele: any) => {
      if (
        ele.hasClass('legend_neutral') ||
        ele.hasClass('legend_positive') ||
        ele.hasClass('legend_negative')
      ) {
        instance.remove(ele)
      }
    })
  }

  private removeTitleAndLegend(instance: cytoscape.Core) {
    instance.nodes().forEach((ele: any) => {
      if (
        ele.hasClass('title') ||
        ele.hasClass('legend_neutral') ||
        ele.hasClass('legend_positive') ||
        ele.hasClass('legend_negative') ||
        ele.hasClass('legend_winner') ||
        ele.hasClass('legend_active') ||
        ele.hasClass('legend_passive')
      ) {
        instance.remove(ele)
      }
    })
  }

  private addTitle(instance: cytoscape.Core, title: string) {
    const rbb = instance.elements().renderedBoundingBox({})
    const node = instance.add({
      classes: 'title',
      data: {
        id: '1234567890',
        type: 'title',
        name: title,
      },
      renderedPosition: {
        x: (rbb.x2 - rbb.x1) / 2 + rbb.x1,
        y: rbb.y1 - 100,
      },
    })
    node.lock()
  }

  private addLegend(instance: cytoscape.Core) {
    if (
      this.hasPositive ||
      this.hasNegative ||
      this.hasWinner ||
      this.hasPassive
    ) {
      const rbb = instance.elements().renderedBoundingBox({})

      const offsetY = 26
      const x = rbb.x1 + 20
      let y = rbb.y2 + 5 * offsetY
      let node = instance.add({
        classes: 'legend_neutral',
        data: {
          id: 'legend_neutral',
          type: 'legend_neutral',
        },
        renderedPosition: {
          x,
          y,
        },
      })
      node.lock()
      y -= 25

      if (this.hasNegative) {
        node = instance.add({
          classes: 'legend_negative',
          data: {
            id: 'legend_negative',
            type: 'legend_negative',
          },
          renderedPosition: {
            x,
            y,
          },
        })
        node.lock()
        y -= offsetY
      }

      if (this.hasPositive) {
        node = instance.add({
          classes: 'legend_positive',
          data: {
            id: 'legend_positive',
            type: 'legend_positive',
          },
          renderedPosition: {
            x,
            y,
          },
        })
        node.lock()
        y -= offsetY
      }

      if (this.hasWinner) {
        node = instance.add({
          classes: 'legend_winner',
          data: {
            id: 'legend_winner',
            type: 'legend_winner',
          },
          renderedPosition: {
            x,
            y,
          },
        })
        node.lock()
        y -= offsetY
      }

      if (this.hasPassive) {
        node = instance.add({
          classes: 'legend_passive',
          data: {
            id: 'legend_passive',
            type: 'legend_passive',
          },
          renderedPosition: {
            x,
            y,
          },
        })
        node.lock()
        y -= offsetY

        node = instance.add({
          classes: 'legend_active',
          data: {
            id: 'legend_active',
            type: 'legend_active',
          },
          renderedPosition: {
            x,
            y,
          },
        })
        node.lock()
        y -= offsetY
      }
    }
  }

  private saveSVG(instance: cytoscape.Core, num: number, suffix: string) {
    // @ts-ignore
    const contents = instance.svg({ scale: 1, full: true })
    const blob = new Blob([contents], { type: 'image/svg+xmlcharset=utf-8' })

    saveAs(blob, `spinne-${this.spiderType}-${num}${suffix}.svg`)
  }

  private savePNG(instance: cytoscape.Core, num: number, suffix: string) {
    const contents = instance.png({ full: true, bg: '#ffffff' })
    saveAs(contents, `spinne-${this.spiderType}-${num}${suffix}.png`)
  }

  // === SETUP ===

  private async reloadAsync() {
    await this.fetchDataAsync()
    if (document.readyState != 'complete') {
      document.addEventListener('DOMContentLoaded', () => {
        this.draw(this.data)
      })
    } else {
      this.draw(this.data)
    }

    if (this.data) {
      const leafs1 = this.data.elements1.filter((e) => e.data.type === 'leaf')
      const leafs2 = this.data.elements1.filter((e) => e.data.type === 'leaf')

      this.hasPositive =
        leafs1.find((e) => e.data.bias > 0 || e.data.attitude > 0) ||
        leafs2.find((e) => e.data.bias > 0)
          ? true
          : false

      this.hasNegative =
        leafs1.find((e) => e.data.bias < 0 || e.data.attitude < 0) ||
        leafs2.find((e) => e.data.bias < 0)
          ? true
          : false

      this.hasWinner =
        leafs1.find((e) => e.data.isWinner) ||
        leafs2.find((e) => e.data.isWinner)
          ? true
          : false

      this.hasPassive =
        leafs1.find((e) => !e.data.isActive) ||
        leafs2.find((e) => !e.data.isActive)
          ? true
          : false
    }
  }

  private async fetchDataAsync() {
    this.project = await ProjectsService.findAsync(this.$route.params.projectId)

    this.data = await DiagramService.spider(
      this.$route.params.projectId,
      this.action
    )
  }

  private showView(view: 'both' | 'first' | 'second') {
    this.view = view
    if (this.data) {
      this.drawInstances(this.data)
      // FIXME: Damit der linke Graph korrekt gezeichnet wird.
      if (view === 'both') {
        this.drawInstances(this.data)
      }
    }
  }

  private draw(data: SpiderData | null) {
    if (data) {
      // Determine if the diagram is split up into two.
      this.isMultiDiagram = data.elements2.length > 1
      this.view = this.isMultiDiagram ? 'both' : 'first'
      this.drawInstances(data)
    }
  }

  private drawInstances(data: SpiderData) {
    Vue.nextTick(() => {
      if (document.getElementById('cy')) {
        window.cyTopic1 = this.drawInstance('cy', data.elements1)
      }
      if (document.getElementById('cz')) {
        if (data.elements2.length > 1) {
          window.cyTopic2 = this.drawInstance('cz', data.elements2)
        } else {
          window.cyTopic2 = this.drawInstance('cz', [])
        }
      }
    })
  }

  private drawInstance(id: string, elements: any[]) {
    // if (window.cyTopic1 && id === "cy") {
    //   window.cyTopic1.json({ elements: elements })
    //   this.layoutGraph(window.cyTopic1)
    //   return window.cyTopic1
    // }
    // if (window.cyTopic2 && id === "cz") {
    //   window.cyTopic2.json({ elements: elements })
    //   this.layoutGraph(window.cyTopic2)
    //   return window.cyTopic2
    // }

    // !!! Lokale cy Instanz funktioniert nicht - Endlosschleife.
    // @ts-ignore
    const instance = cytoscape({
      container: document.getElementById(id),
      // @ts-ignore
      style,
      elements,
      wheelSensitivity: 0.2,
    })

    instance.ready(() => {
      this.setupEvents(instance)
      instance
        .style()
        // @ts-ignore
        .selector(':selected')
      // .css({
      //   "overlay-color": "black",
      //   "overlay-padding": "5",
      //   "overlay-opacity": "0.25",
      // })

      this.layoutGraph(instance)
    })

    return instance
  }

  private layoutGraph(instance: cytoscape.Core) {
    const layout = instance.layout({
      name: 'concentric',
      startAngle: (5 / 4) * Math.PI,
      clockwise: true,
      animate: false,
      concentric: (node: any) => {
        switch (node.data('type')) {
          case 'root':
            return 10
          case 'node':
            return 5
          default:
            return 1
        }
      },
      stop: () => {
        const layout2 = instance.layout({
          name: 'cose-bilkent',
          // @ts-ignore
          animate: false,
          randomize: false,
          padding: 40,
          nodeDimensionsIncludeLabels: true,
          stop: () => {
            cytoService.removeDeadEdges(instance)
            this.layoutGroups(instance)
          },
        })
        layout2.run()
      },
    })
    layout.run()
  }

  private layoutGroups(instance: cytoscape.Core) {
    instance
      .nodes()
      .filter((n: any) => 'group'.includes(n.data('type')))
      .forEach((group: any) => {
        const leafs = this.getLeafsInGroup(instance, group.data('id'))
        const numColumns = Math.ceil(leafs.length / 5)
        const numRows =
          leafs.length > 5 ? Math.ceil(leafs.length / 2) : leafs.length
        const groupsLayout = leafs.layout({
          name: 'grid',
          fit: false,
          cols: numColumns,
          position: (node: any) => {
            return {
              // @ts-ignore
              row: leafs.indexOf(node) % numRows,
              // @ts-ignore
              col: Math.floor(leafs.indexOf(node) / numRows),
            }
          },
          animate: false,
          boundingBox: leafs.boundingBox({}),
          condense: true,
          stop: () => {
            instance.fit(undefined, this.PADDING)
          },
        })
        groupsLayout.run()
      })
  }

  private getLeafsInGroup(instance: cytoscape.Core, groupId: string) {
    return instance.nodes().filter((n: any) => {
      return 'leaf'.includes(n.data('type')) && n.data('parent') === groupId
    })
  }

  private setupEvents(instance: cytoscape.Core) {
    instance.on('mouseup', 'node', (evt: any) => {
      const self = this
      instance.nodes().forEach((ele: any, index: number) => {
        if (ele.hasClass('delete') || ele.hasClass('deleteNode')) {
          if (this.deleteButtonTriggered(ele, evt)) {
            if (confirm('Soll das Element wirklich gelöscht werden?')) {
              self.delete(ele.data('className'), ele.data('dbId'))
              this.$bus.$emit('node:unselect')
            }
          }
        }
      })
    })

    instance.on('mousedown', (evt: any) => {
      if (evt.target.id === undefined) {
        this.unselectAllNodes()
      }
    })

    instance.on('tap', 'node', (evt: EventObject) => {
      this.toggleNode(instance, evt)
    })

    instance.on('cdndgrab', this.grabNode)
    // @ts-ignore
    instance.on('cdnddrop', this.dropNode)
  }

  private async delete(className: NodeClassName, dbId: string) {
    await DiagramService.deleteNode(className, dbId)
    await this.reloadAsync()
  }

  private deleteButtonTriggered(ele: any, evt: any) {
    if (ele.data('type') === 'node') {
      if (
        evt.position.x > evt.target.position().x + 135 &&
        evt.position.x < evt.target.position().x + 200
      ) {
        return true
      }
    } else if (ele.data('type') === 'leaf') {
      if (
        evt.position.x > evt.target.position().x + 100 &&
        evt.position.x < evt.target.position().x + 125
      ) {
        return true
      }
    }
    return false
  }

  private toggleNode(instance: cytoscape.Core, evt: any) {
    const node = evt.target
    this.unselectAllNodes()
    if (this.selected === null || this.selected.dbId !== node.data('dbId')) {
      this.selectNode(node)
    }
  }

  private unselectAllNodes() {
    cytoService.unselectAllNodes(window.cyTopic1)
    cytoService.unselectAllNodes(window.cyTopic2)
    this.selected = null
  }

  private selectNode(node: any) {
    // We cannot edit cytoscape-groups of items.
    if (node.data('type') === 'group') {
      return
    }

    node.css('border-width', '1')
    // !!! Cannot do this.selected = node. This would end in an infinite loop.
    this.selected = this.newNode(node)
    switch (node.data('type')) {
      case 'leaf':
        node.toggleClass('delete', true)
        // node.addClass('shadow')
        break
      case 'node':
        node.toggleClass('deleteNode', true)
        break
      default:
        console.log(`Unsupported node type ${node.data('type')}.`)
    }
    this.sendEditNodeEvent(this.selected)
  }

  private unselectNode(node: any) {
    node.css('border-width', '1')
    node.toggleClass('delete', false)
    this.selected = null
    node.unselect()
    this.$bus.$emit('node:unselect')
  }

  private grabNode(evt: any) {
    this.sourceGroup = evt.target.parent().data('id')
  }

  private dropNode(
    instance: cytoscape.Core,
    event: EventObject,
    dropTarget: any
  ) {
    let target = dropTarget.id()
    if (target === this.sourceGroup) {
      // drag/drop within same group => no action necessary
      return
    }
    target = target.replace('group-', '')
    const group = instance.filter(`node[id='${target}']`)
    this.addMemberToGroup(group.data('dbId'), event.target.data('dbId'))

    const location = this.$route.fullPath
    this.$router.replace('/')
    this.$nextTick(() => this.$router.replace(location))
  }

  private async addMemberToGroup(groupId: string, memberId: string) {
    const res = await DiagramService.topicGroupCreateMember(groupId, memberId)
    await this.reloadAsync()
  }

  // === FAB BUTTON ACTIONS ===

  private clickAdd() {
    if (this.selected) {
      if (Nodes.isTopic(this.selected.className)) {
        // + button deaktiviert.
      } else if (Nodes.isTopicGroup(this.selected.className)) {
        this.sendCreateNodeEvent(this.selected, 'Topic')
      } else if (Nodes.isProjectStakeholder(this.selected.className)) {
        // + button deaktiviert.
      } else if (Nodes.isProjectStakeholderGroup(this.selected.className)) {
        this.sendCreateNodeEvent(this.selected, 'ProjectStakeholder')
      } else if (Nodes.isAction(this.selected.className)) {
        // + button deaktiviert.
      } else if (Nodes.isActionGroup(this.selected.className)) {
        this.sendCreateNodeEvent(this.selected, 'Action')
      } else {
        this.createNewGroup(this.selected)
      }
    } else if (this.project) {
      this.createNewGroup(new Node('root', 'Project', this.project.id))
    }
  }

  private clickZoomIn() {
    cytoService.zoomIn(window.cyTopic1)
    cytoService.zoomIn(window.cyTopic2)
  }

  private clickZoomOut() {
    cytoService.zoomOut(window.cyTopic1)
    cytoService.zoomOut(window.cyTopic2)
  }

  private clickResetZoom() {
    window.cyTopic1.fit(undefined, this.PADDING)
    window.cyTopic2.fit(undefined, this.PADDING)
  }

  private createNewGroup(node: Node) {
    switch (this.action) {
      case 'projectTopicGroupsTopics':
        this.sendCreateNodeEvent(node, 'TopicGroup')
        break
      case 'projectStakeholderGroupStakeholders':
        this.sendCreateNodeEvent(node, 'ProjectStakeholderGroup')
        break
      case 'projectActionGroupsActions':
        this.sendCreateNodeEvent(node, 'ActionGroup')
        break
    }
  }

  private newNode(node: any) {
    const type = node.data('type')
    const className = node.data('className')
    const dbId = node.data('dbId')
    return new Node(type, className, dbId)
  }

  private sendCreateNodeEvent(node: Node, className: NodeClassName) {
    if (this.project) {
      const evt = new CreateNodeEvent(this.project, node, className)
      this.$bus.$emit('node:create', evt)
    }
  }

  private sendEditNodeEvent(node: Node) {
    if (this.project) {
      const evt = new EditNodeEvent(this.project, node)
      this.$bus.$emit('node:edit', evt)
    }
  }

  private activated() {
    this.$bus.$on('reload-spider', async () => {
      await this.reloadAsync()
    })
  }

  private deactivated() {
    this.$bus.$off('reload-spider')
  }

  private beforeRouteEnter(to: any, from: any, next: any) {
    next((vm: Vue) => {
      vm.$bus.$emit('sidemenu:mode', 'none')
    })
  }
}
